'use client'

import React, { KeyboardEvent, useCallback, useEffect, useState } from 'react'

import Autoplay, { AutoplayOptionsType } from 'embla-carousel-autoplay'
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react'

import styles from './Carousel.module.css'

const autoplayOptionsInitial: AutoplayOptionsType = {
  delay: 5000,
  stopOnInteraction: false,
  rootNode: (emblaRoot: HTMLElement) => emblaRoot.parentElement,
}

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]

export type CarouselProps = {
  options?: CarouselOptions
  autoPlay?: boolean
  setApi?: (api: CarouselApi) => void
  autoplayOptions?: AutoplayOptionsType
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: (e?: React.SyntheticEvent) => void
  scrollNext: (e?: React.SyntheticEvent) => void
  canScrollPrev: boolean
  canScrollNext: boolean
  onDotButtonClick: (e: MouseEvent, index: number) => void
  scrollSnaps: Array<number>
  selectedIndex: number
} & CarouselProps

export const CarouselContext = React.createContext<CarouselContextProps | null>(null)

export function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />')
  }

  return context
}

export function CarouselProvider({
  id,
  options,
  setApi,
  autoPlay = false,
  children,
  autoplayOptions,
  theme,
  ...props
}: React.ComponentProps<'div'> & CarouselProps & { theme?: typeof styles; id: string }) {
  const mergedTheme = { ...styles, ...theme }

  const [carouselRef, api] = useEmblaCarousel(
    {
      axis: 'x',
      loop: true,
      ...options,
    },
    [Autoplay({ ...autoplayOptionsInitial, ...autoplayOptions, playOnInit: autoPlay })]
  )
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<Array<number>>([])

  const onSelect = useCallback((api: CarouselApi) => {
    if (!api) return
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
    setSelectedIndex(api.selectedScrollSnap())
  }, [])

  const onInit = useCallback((api: CarouselApi) => {
    if (!api) return
    setScrollSnaps(api.scrollSnapList())
  }, [])

  const scrollPrev = useCallback(
    (e?: React.SyntheticEvent) => {
      e?.preventDefault()
      api?.scrollPrev()
    },
    [api]
  )

  const scrollNext = useCallback(
    (e?: React.SyntheticEvent) => {
      e?.preventDefault()
      api?.scrollNext()
    },
    [api]
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        scrollPrev(event)
      } else if (event.key === 'ArrowRight') {
        scrollNext(event)
      }
    },
    [scrollPrev, scrollNext]
  )

  const onDotButtonClick = useCallback(
    (e: MouseEvent, index: number) => {
      if (!api) return
      e.stopPropagation()
      e.preventDefault()
      api.scrollTo(index)
    },
    [api]
  )

  useEffect(() => {
    if (!api || !setApi) return
    setApi(api)
  }, [api, setApi])

  useEffect(() => {
    if (!api) return
    onSelect(api)
    onInit(api)

    api.on('reInit', onInit)
    api.on('reInit', onSelect)
    api.on('select', onSelect)

    return () => {
      api?.off('select', onSelect)
    }
  }, [api, onSelect, onInit])

  // Preventing the default action (e.g., navigating to a link).
  /* useEffect(() => {
    const carouselWrapper = document.getElementById(id)

    const handleClick = (e: MouseEvent) => {
      e.preventDefault()
    }

    if (carouselWrapper) {
      carouselWrapper.addEventListener('click', handleClick)

      return () => {
        carouselWrapper.removeEventListener('click', handleClick)
      }
    }
  }, []) */

  return (
    <CarouselContext.Provider
      value={{ api, carouselRef, canScrollPrev, canScrollNext, selectedIndex, scrollSnaps, scrollPrev, scrollNext, onDotButtonClick }}>
      <div
        id={id}
        onKeyDownCapture={handleKeyDown}
        role='region'
        aria-roledescription='carousel'
        data-slot='carousel'
        className={mergedTheme.carouselWrapper}
        {...props}>
        {children}
      </div>
    </CarouselContext.Provider>
  )
}
